import { Box } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  StyledAddressFormContainer,
  StyledBoxPersonal,
  StyledBoxPersonalTab,
  StyledBoxTab,
  StyledBoxTabValue,
  StyledBoxuploadContainer,
  StyledBoxWrapper,
  StyledFormActionBox,
  StyledInput,
  StyledProfileFieldGrid,
  StyledProfileFormBox,
  StyledTypographyEdit,
  StyledTypographyTab,
  StyledTypographyTabValue,
  StyledTypographyTabValueUpdate,
  StyledTypographyUpdate,
} from "./style";
import theme from "../../theme";
import CustomButton from "../../components/common/button/CustomButton";
import { UploadIcon } from "../../assets/Icons/UploadIcon";
import Grid from "@mui/material/Grid2";
import { Form, Formik } from "formik";
import CustomDropdown from "../../components/common/common-dropdown/CustomDropdown";
import CustomInput from "../../components/common/input/CustomInput";
import * as Yup from "yup";
import AddressDefault from "../../components/address-default/AddressDefault";
import PersonalInformation from "../../components/profile/PersonalInformation";
import ProfileSuccess from "../../components/modals/success-profile/ProfileSuccess";
import { GET_ADDRESS_LIST } from "../../actions/AddressActions";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_PROFILE_DETAILS, UPLOAD } from "../../actions/AuthActions";
import CircularProgress from "@mui/material/CircularProgress";

import { getPhoneValidation } from "../../utils/countryRules";
import { useLocation, useNavigate } from "react-router-dom";
import ProfilePlaceholder from "../../components/common/profile-placeholder/ProfilePlaceholder";
import toast from "react-hot-toast";

const MAX_IMAGE_SIZE = 1.5 * 1024 * 1024; // 1.5 MB

const GENDER_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProfilePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewProfile, setViewProfile] = useState(false);
  const [currentCountryCode, setCurrentCountryCode] = React.useState("+91");
  const [altCurrentCountryCode, setAltCurrentCountryCode] =
    React.useState("+91");

  const [value, setValue] = useState("personal");

  const profileDetails = useSelector((state) => state?.auth?.profileDetails);
  const isPersonalShopper = profileDetails?.user_type === "personal_shopper";
  const isSubmitting = useSelector((state) => state?.auth?.isLoading);

  const dispatch = useDispatch();
  const submitLock = useRef(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Full Name is required"),
    email: Yup.string()
      .required("Email Address is required")
      .test("valid-email", "Enter a valid email", (value) =>
        EMAIL_REGEX.test(String(value || "").trim())
      ),
    phone_number: Yup.string()
      .required("Phone number is required")
      .test(getPhoneValidation(currentCountryCode)),
    alternative_phone_number: Yup.string()
      .nullable()
      .test(getPhoneValidation(altCurrentCountryCode)),
    gender: Yup.string()
      .required("Gender is required")
      .oneOf(["male", "female"], "Please select a valid gender"),
  });

  const getAddress = useCallback(() => {
    dispatch({ type: GET_ADDRESS_LIST });
  }, [dispatch]);

  useEffect(() => {
    getAddress();
  }, [getAddress]);

  useEffect(() => {
    if (profileDetails?.is_profile_completed) {
      setViewProfile(false);
    }
  }, [profileDetails?.is_profile_completed]);

  useEffect(() => {
    if (location.state?.editProfile) {
      setViewProfile(true);
    }
  }, [location.state?.editProfile]);

  const [openProfileSuccess, setOpenProfileSuccess] = useState(false);
  const uploadImageLink = useSelector((state) => state.auth?.uploadSuccess);
  const isUploading = useSelector((state) => state.auth?.isUploading);
  const [profileImage, setProfileImage] = useState("");

  useEffect(()=>{
    setProfileImage(uploadImageLink);
  },[uploadImageLink])

  useEffect(() => {
    if (profileDetails?.profile_image) {
      setProfileImage(profileDetails?.profile_image || "");
    }
  }, [profileDetails?.profile_image]);

  const handleOpenProfileSuccess = () => setOpenProfileSuccess(true);
  const handleCloseProfileSuccess = () => setOpenProfileSuccess(false);

  const fileInputRef = useRef();

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleCancelEdit = () => {
    setViewProfile(false);
    if (location.state?.editProfile) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };

  const handleProfileSubmit = (values) => {
    if (submitLock.current || isSubmitting) return;
    submitLock.current = true;

    dispatch({
      type: UPDATE_PROFILE_DETAILS,
      payload: {
        ...values,
        phone_number: values?.phone_number
          ? `${currentCountryCode} ${values.phone_number}`
          : "",
        alternative_phone_number: values?.alternative_phone_number
          ? `${altCurrentCountryCode} ${values.alternative_phone_number}`
          : "",
        profile_image: profileImage,
        ...(isPersonalShopper ? { is_profile_completed: true } : {}),
        onSuccess: () => {
          submitLock.current = false;
          if (isPersonalShopper) {
            setViewProfile(false);
            if (location.state?.editProfile) {
              navigate(location.pathname, { replace: true, state: null });
            }
            handleOpenProfileSuccess();
            return;
          }
          setValue("address");
        },
        onError: () => {
          submitLock.current = false;
        },
      },
    });
  };
  useEffect(() => {
    if (profileDetails?.phone_number) {
      setCurrentCountryCode(
        profileDetails?.phone_number?.includes(" ")
          ? profileDetails?.phone_number?.split(" ")?.[0]
          : "+91"
      );
    }
    if (profileDetails?.alternative_phone_number) {
      setAltCurrentCountryCode(
        profileDetails?.alternative_phone_number?.includes(" ")
          ? profileDetails?.alternative_phone_number?.split(" ")?.[0]
          : "+91"
      );
    }
  }, [profileDetails?.phone_number, profileDetails?.alternative_phone_number]);

  const getMaxDateFor18Plus = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  return (
    <>
      {viewProfile === true ? (
        <StyledAddressFormContainer>
          <StyledBoxWrapper>
            <Box>
              <StyledTypographyEdit>Edit Profile</StyledTypographyEdit>
              <StyledTypographyUpdate>
                Update your profile information
              </StyledTypographyUpdate>
            </Box>

            <CustomButton
              padding="17px 39px"
              fontSize={theme.typography.base.fontSize}
              height="54px"
              value={value}
              btnLabel="Cancel"
              btntype={"white"}
              color={theme.palette.custom.lockerValueColor}
              fontWeight={theme.typography.fontWeight.medium}
              border={theme.borders.defaultButton}
              onClick={handleCancelEdit}
            />
          </StyledBoxWrapper>
          <StyledBoxPersonalTab>
            <StyledBoxTab>
              <StyledTypographyTab
                value={value === "personal"}
                onClick={() => setValue("personal")}
              >
                Personal Information
              </StyledTypographyTab>
              <StyledTypographyTab
                value={value === "address"}
                onClick={() => setValue("address")}
              >
                Default Address
              </StyledTypographyTab>
            </StyledBoxTab>
            {value === "personal" ? (
              <StyledBoxPersonal>
                <StyledBoxTabValue>
                  <StyledTypographyTabValue>
                    Personal Information
                  </StyledTypographyTabValue>
                  <StyledTypographyTabValueUpdate>
                    Update your profile information
                  </StyledTypographyTabValueUpdate>
                </StyledBoxTabValue>
                <StyledBoxuploadContainer>
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        loading="lazy"
                        className="profile-image"
                        style={{
                          opacity: isUploading ? 0.4 : 1,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <ProfilePlaceholder size={150} showBorder />
                    )}
                    {isUploading && (
                      <CircularProgress
                        size={40}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-20px",
                          marginLeft: "-20px",
                          color: theme.palette.custom.uploadColor,
                        }}
                      />
                    )}
                  </Box>
                  <CustomButton
                    fontSize={theme.typography.extraLarge.fontSize}
                    startIcon={isUploading ? <CircularProgress size={16} sx={{ color: theme.palette.custom.uploadColor }} /> : <UploadIcon />}
                    btntype="white"
                    btnLabel={isUploading ? "Uploading..." : "Upload Photo"}
                    type="file"
                    padding="8px 21px"
                    height="48px"
                    border="1px solid #E17B0B"
                    color={theme.palette.custom.uploadColor}
                    onClick={!isUploading ? handleButtonClick : undefined}
                  />
                  <StyledInput
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > MAX_IMAGE_SIZE) {
                        toast.error("Image size must be under 1.5 MB.");
                        e.target.value = "";
                        return;
                      }
                      dispatch({
                        type: UPLOAD,
                        payload: file,
                      });
                    }}
                    accept="image/*"
                  />
                </StyledBoxuploadContainer>
                <Box>
                  <Formik
                    initialValues={{
                      name:
                        profileDetails?.name ||
                        (
                          (profileDetails?.first_name || "") +
                          " " +
                          (profileDetails?.last_name || "")
                        ).trim(),
                      email: profileDetails?.email || "",
                      phone_number: profileDetails?.phone_number
                        ? profileDetails?.phone_number?.split(" ")?.[1]
                        : "",
                      alternative_phone_number:
                        profileDetails?.alternative_phone_number
                          ? profileDetails?.alternative_phone_number?.split(
                              " "
                            )?.[1]
                          : "",
                      date_of_birth:
                        profileDetails?.date_of_birth?.split("T")?.[0] || "",
                      gender: profileDetails?.gender || "",
                    }}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    onSubmit={handleProfileSubmit}
                  >
                    {({
                      errors,
                      handleChange,
                      handleBlur,
                      values,
                      touched,
                      setFieldValue,
                    }) => (
                      <Form>
                        <StyledProfileFormBox>
                          <Grid container columnSpacing={2}>
                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomInput
                                isprofile="true"
                                height="28px"
                                required
                                customLabel="Full Name"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                              />
                            </StyledProfileFieldGrid>
                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomInput
                                isprofile="true"
                                height="28px"
                                required
                                customLabel="Email Address"
                                name="email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                              />
                            </StyledProfileFieldGrid>
                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomInput
                                required
                                isprofile="true"
                                height="28px"
                                customLabel="Phone Number"
                                type="text"
                                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                                name="phone_number"
                                value={values.phone_number}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                  setFieldValue("phone_number", val);
                                }}
                                onBlur={handleBlur}
                                error={
                                  touched.phone_number &&
                                  Boolean(errors.phone_number)
                                }
                                helperText={
                                  touched.phone_number && errors.phone_number
                                }
                                isPhoneNumber={true}
                                currentCountryCode={currentCountryCode}
                                setCurrentCountryCode={setCurrentCountryCode}
                              />
                            </StyledProfileFieldGrid>
                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomInput
                                isprofile="true"
                                type="number"
                                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                                height="28px"
                                customLabel="Alternative Phone Number"
                                name="alternative_phone_number"
                                value={values.alternative_phone_number}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                  setFieldValue("alternative_phone_number", val);
                                }}
                                onBlur={handleBlur}
                                error={
                                  touched.alternative_phone_number &&
                                  Boolean(errors.alternative_phone_number)
                                }
                                helperText={
                                  touched.alternative_phone_number &&
                                  errors.alternative_phone_number
                                }
                                isPhoneNumber={true}
                                currentCountryCode={altCurrentCountryCode}
                                setCurrentCountryCode={setAltCurrentCountryCode}
                              />
                            </StyledProfileFieldGrid>

                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomInput
                                isprofile="true"
                                height="28px"
                                type="date"
                                customLabel="Date of Birth"
                                name="date_of_birth"
                                value={values.date_of_birth}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                max={getMaxDateFor18Plus()}
                              />
                            </StyledProfileFieldGrid>
                            <StyledProfileFieldGrid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                              <CustomDropdown
                                isprofile
                                required
                                customLabel="Gender"
                                placeholder="Select gender"
                                options={GENDER_OPTIONS}
                                name="gender"
                                value={values.gender}
                                setFieldValue={setFieldValue}
                                onBlur={handleBlur}
                                error={
                                  touched.gender && Boolean(errors.gender)
                                }
                                helperText={
                                  touched.gender && errors.gender
                                }
                              />
                            </StyledProfileFieldGrid>
                          </Grid>

                          <StyledFormActionBox>
                            <CustomButton
                              fontSize={theme.typography.base.fontSize}
                              height="54px"
                              value={value}
                              btnLabel="Cancel"
                              btntype={"white"}
                              color={theme.palette.custom.lockerValueColor}
                              fontWeight={theme.typography.fontWeight.medium}
                              border={theme.borders.defaultButton}
                              onClick={handleCancelEdit}
                            />

                            <CustomButton
                              fontSize={theme.typography.base.fontSize}
                              height="54px"
                              value={value}
                              type="submit"
                              btnLabel="Save changes"
                              btntype={"orderCards"}
                              color={theme.palette.custom.white}
                              fontWeight={theme.typography.fontWeight.medium}
                              border={theme.borders.defaultButton}
                              disabled={isSubmitting}
                              isLoading={isSubmitting}
                            />
                          </StyledFormActionBox>
                        </StyledProfileFormBox>
                      </Form>
                    )}
                  </Formik>
                </Box>
              </StyledBoxPersonal>
            ) : (
              <>
                <StyledBoxPersonal>
                  <StyledBoxTabValue>
                    <StyledTypographyTabValue>
                      Your Addressess
                    </StyledTypographyTabValue>
                    <StyledTypographyTabValueUpdate>
                      Update or add new addresses
                    </StyledTypographyTabValueUpdate>
                  </StyledBoxTabValue>
                  <Box>
                    <AddressDefault
                      setViewProfile={setViewProfile}
                      viewProfile={viewProfile}
                      handleOpen={handleOpenProfileSuccess}
                    />
                  </Box>
                </StyledBoxPersonal>
              </>
            )}
          </StyledBoxPersonalTab>
        </StyledAddressFormContainer>
      ) : (
        <PersonalInformation setViewProfile={setViewProfile} />
      )}

      <ProfileSuccess
        open={openProfileSuccess}
        handleOpen={handleOpenProfileSuccess}
        handleClose={handleCloseProfileSuccess}
      />
    </>
  );
};

export default ProfilePage;
